import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, Input, Button } from '../components/ui'
import { PageHeader } from '../../ui'
import { listSavedRecipients, createSavedRecipient, updateSavedRecipient, deleteSavedRecipient } from '../lib/ixverifyApi'
import { useIXVerifyAuth } from '../contexts'
import type { SavedRecipient } from '../types'

export function SavedRecipients() {
  const { user, loading: authLoading } = useIXVerifyAuth()
  const [recipients, setRecipients] = useState<SavedRecipient[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [showAddForm, setShowAddForm] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  // Form state
  const [formEmail, setFormEmail] = useState('')
  const [formLabel, setFormLabel] = useState('')
  const [formError, setFormError] = useState('')

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return

    if (user?.site_id) {
      fetchRecipients()
    } else {
      setLoading(false)
    }
  }, [user?.id, user?.site_id, authLoading])

  const fetchRecipients = async () => {
    if (!user?.site_id) return

    setLoading(true)
    try {
      const { data, error } = await listSavedRecipients(user.site_id)

      if (error) throw error
      setRecipients(data || [])
    } catch (error) {
      console.error('Error fetching recipients:', error)
    } finally {
      setLoading(false)
    }
  }

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const resetForm = () => {
    setFormEmail('')
    setFormLabel('')
    setFormError('')
    setShowAddForm(false)
    setEditingId(null)
  }

  const handleAdd = async () => {
    if (!user?.site_id) return

    // Validate email
    if (!formEmail.trim()) {
      setFormError('Email is required')
      return
    }
    if (!validateEmail(formEmail.trim())) {
      setFormError('Please enter a valid email address')
      return
    }

    // Check for duplicates
    const existingRecipient = recipients.find(
      (r) => r.email.toLowerCase() === formEmail.trim().toLowerCase()
    )
    if (existingRecipient) {
      setFormError('This email already exists')
      return
    }

    setSaving(true)
    setFormError('')

    try {
      const { data, error } = await createSavedRecipient({
        site_id: user.site_id,
        email: formEmail.trim(),
        label: formLabel.trim() || null,
      })

      if (error) throw error

      setRecipients([...recipients, data])
      resetForm()
    } catch (error) {
      console.error('Error adding recipient:', error)
      setFormError('Failed to add recipient. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdate = async (id: string) => {
    // Validate email
    if (!formEmail.trim()) {
      setFormError('Email is required')
      return
    }
    if (!validateEmail(formEmail.trim())) {
      setFormError('Please enter a valid email address')
      return
    }

    // Check for duplicates (excluding current recipient)
    const existingRecipient = recipients.find(
      (r) => r.id !== id && r.email.toLowerCase() === formEmail.trim().toLowerCase()
    )
    if (existingRecipient) {
      setFormError('This email already exists')
      return
    }

    setSaving(true)
    setFormError('')

    try {
      const { error } = await updateSavedRecipient(id, {
        email: formEmail.trim(),
        label: formLabel.trim() || null,
      })

      if (error) throw error

      setRecipients(
        recipients.map((r) =>
          r.id === id
            ? { ...r, email: formEmail.trim(), label: formLabel.trim() || null }
            : r
        )
      )
      resetForm()
    } catch (error) {
      console.error('Error updating recipient:', error)
      setFormError('Failed to update recipient. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      const { error } = await deleteSavedRecipient(id)

      if (error) throw error

      setRecipients(recipients.filter((r) => r.id !== id))
      setDeleteConfirmId(null)
    } catch (error) {
      console.error('Error deleting recipient:', error)
    }
  }

  const startEditing = (recipient: SavedRecipient) => {
    setEditingId(recipient.id)
    setFormEmail(recipient.email)
    setFormLabel(recipient.label || '')
    setFormError('')
    setShowAddForm(false)
  }

  const startAdding = () => {
    setShowAddForm(true)
    setEditingId(null)
    setFormEmail('')
    setFormLabel('')
    setFormError('')
  }

  // No site assigned
  if (!user?.site_id) {
    return (
      <div className="max-w-7xl mx-auto space-y-6">
        <PageHeader title="Saved Recipients" subtitle="Manage email recipients for verification reports" />
        <Card>
          <CardContent className="p-6 text-center">
            <svg className="w-12 h-12 mx-auto text-yellow-500 mb-4 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-gray-600 mb-2 dark:text-gray-400">No site assigned to your account</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Contact your administrator to be assigned to a site before managing recipients.
            </p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <PageHeader
        title="Saved Recipients"
        subtitle="Manage email recipients for verification reports"
        actions={
          !showAddForm && !editingId ? (
            <Button size="sm" onClick={startAdding}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Recipient
            </Button>
          ) : undefined
        }
      />

      {/* Add/Edit Form */}
      {(showAddForm || editingId) && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-gray-900 dark:text-gray-100">
              {editingId ? 'Edit Recipient' : 'Add New Recipient'}
            </h2>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Email Address *
              </label>
              <Input
                type="email"
                placeholder="email@example.com"
                value={formEmail}
                onChange={(e) => {
                  setFormEmail(e.target.value)
                  setFormError('')
                }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 dark:text-gray-300">
                Label (optional)
              </label>
              <Input
                type="text"
                placeholder="e.g., Dr. Smith, Pharmacy Lead"
                value={formLabel}
                onChange={(e) => setFormLabel(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                A label helps identify this recipient in the list
              </p>
            </div>

            {formError && (
              <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
            )}

            <div className="flex gap-3">
              <Button
                onClick={() => editingId ? handleUpdate(editingId) : handleAdd()}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Saving...
                  </>
                ) : editingId ? (
                  'Update Recipient'
                ) : (
                  'Add Recipient'
                )}
              </Button>
              <Button variant="outline" onClick={resetForm} disabled={saving}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recipients List */}
      <Card>
        <CardHeader>
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Recipients ({recipients.length})
          </h2>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
            </div>
          ) : recipients.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto text-gray-300 mb-4 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-gray-500 mb-2 dark:text-gray-400">No saved recipients yet</p>
              <p className="text-sm text-gray-400 mb-4 dark:text-gray-500">
                Add recipients to quickly select them when sending verification reports
              </p>
              {!showAddForm && (
                <Button onClick={startAdding}>Add First Recipient</Button>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {recipients.map((recipient) => (
                <div
                  key={recipient.id}
                  className="py-4 flex items-center justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-gray-400 flex-shrink-0 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate dark:text-gray-100">{recipient.email}</p>
                        {recipient.label && (
                          <p className="text-sm text-gray-500 truncate dark:text-gray-400">{recipient.label}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 ml-4">
                    {deleteConfirmId === recipient.id ? (
                      <>
                        <span className="text-sm text-red-600 mr-2 dark:text-red-400">Delete?</span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(recipient.id)}
                          className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                        >
                          Yes
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteConfirmId(null)}
                        >
                          No
                        </Button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => startEditing(recipient)}
                          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-300 dark:hover:bg-gray-700"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setDeleteConfirmId(recipient.id)}
                          className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-500 dark:hover:text-red-400 dark:hover:bg-red-900/20"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-primary-500 flex-shrink-0 mt-0.5 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Saved recipients will appear as quick-select options when sending verification reports.
                All recipients for your site are shared among site users.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
